import { createContext, useContext, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/shared/hooks/use-toast";
import { logger } from '@/shared/utils/logger';
import { stopTimingBatcher } from '@/utils/operationTimer';
import { setImpersonationInProgress } from './useAuthState';
interface AuthMethodsContextType {
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, nome: string, cognome: string) => Promise<{ error: Error | null, data: any }>;
  signOut: () => Promise<void>;
  loginAsTestUser: (email: string) => Promise<{ success: boolean; landingPage?: string }>;
  impersonateUser: (userId: string) => Promise<{
    success: boolean;
    data?: {
      session_id: string;
      target_user_id: string;
      target_user_name: string;
      target_user_email: string;
      admin_email: string;
      expires_at: string;
    };
  }>;
}

const AuthMethodsContext = createContext<AuthMethodsContextType | undefined>(undefined);

/**
 * @description Context provider that exposes authentication methods: signIn, signUp, signOut, loginAsTestUser, and impersonateUser.
 * Handles Supabase auth operations with error handling and toast notifications.
 * @param {{ children: ReactNode }} props - React children to wrap.
 */
export const AuthMethodsProvider = ({ children }: { children: ReactNode }) => {
  // Login
  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      return { error };
    } catch (error) {
      logger.error("Errore durante il login:", error);
      return { error: error as Error };
    }
  };

  // Registrazione
  const signUp = async (email: string, password: string, nome: string, cognome: string) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            nome,
            cognome,
          },
        },
      });

      return { data, error };
    } catch (error) {
      logger.error("Errore durante la registrazione:", error);
      return { data: null, error: error as Error };
    }
  };

  // Login come utente di test
  const loginAsTestUser = async (email: string): Promise<{ success: boolean; landingPage?: string }> => {
    try {
      // C10 Security Fix: La password DEVE essere configurata nell'ambiente
      const password = import.meta.env.VITE_TEST_PASSWORD;

      if (!password) {
        toast({
          title: "Configurazione mancante",
          description: "VITE_TEST_PASSWORD non configurata. Funzione disponibile solo in ambiente di sviluppo.",
          variant: "destructive",
        });
        logger.warn('🔐 Test user login aborted: VITE_TEST_PASSWORD not configured');
        return { success: false };
      }

      logger.debug(`🔐 Attempting to login as test user: ${email}`);

      // Effettua il logout prima di accedere come utente di test
      await supabase.auth.signOut();

      // Accedi come utente di test
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        logger.error("Login as test user failed:", error);

        // Gestione specifica per credenziali non valide
        if (error.message.includes("Invalid login credentials")) {
          toast({
            title: "Credenziali non valide",
            description: `L'utente ${email} potrebbe avere una password diversa da quella configurata nell'ambiente. Verifica le credenziali dell'utente.`,
            variant: "destructive",
          });
        } else {
          toast({
            title: "Errore di accesso",
            description: error.message || "Non è stato possibile accedere come utente di test",
            variant: "destructive",
          });
        }
        return { success: false };
      }

      logger.debug(`✅ Successfully logged in as: ${email}`);

      toast({
        title: "Accesso effettuato",
        description: `Hai effettuato l'accesso come ${email}`,
      });

      // Determine landing page based on user role
      const { data: { user: currentUser } } = await supabase.auth.getUser();

      if (currentUser) {
        // Get user's roles with landing page
        const { data: userRolesData } = await supabase
          .from('user_roles')
          .select('roles!inner(role_code, default_landing_page)')
          .eq('user_id', currentUser.id);

        if (userRolesData && userRolesData.length > 0) {
          // Use the first role's landing page
          const landingPage = userRolesData[0].roles?.default_landing_page || '/shop';
          return { success: true, landingPage };
        }
      }

      return { success: true, landingPage: '/shop' };

    } catch (error) {
      logger.error("Errore durante l'accesso come utente di test:", error);
      toast({
        title: "Errore",
        description: "Si è verificato un errore imprevisto durante l'accesso",
        variant: "destructive",
      });
      return { success: false };
    }
  };

  // Impersonate user via edge function (secure method)
  // Returns impersonation data for the caller to handle
  const impersonateUser = async (userId: string): Promise<{
    success: boolean;
    data?: {
      session_id: string;
      target_user_id: string;
      target_user_name: string;
      target_user_email: string;
      admin_email: string;
      expires_at: string;
    };
  }> => {
    try {
      logger.debug(`🔐 Attempting to impersonate user: ${userId}`);

      // C9 Security Fix: Check rate limit before proceeding
      const { data: rateLimitData, error: rateLimitError } = await supabase.rpc('rpc_check_impersonation_rate_limit');

      if (rateLimitError) {
        logger.warn('Rate limit check failed, proceeding with caution:', rateLimitError.message);
        // Continue anyway - the edge function has its own checks
      } else if (rateLimitData && !rateLimitData.allowed) {
        const retryMinutes = Math.ceil(rateLimitData.retry_after_minutes || 60);
        toast({
          title: "Rate limit superato",
          description: `Hai raggiunto il limite di ${rateLimitData.max_sessions} sessioni di impersonation per ora. Riprova tra ${retryMinutes} minuti.`,
          variant: "destructive",
        });
        logger.warn(`🔐 Rate limit exceeded: ${rateLimitData.sessions_used}/${rateLimitData.max_sessions} sessions used`);
        return { success: false };
      }

      // Call edge function to get impersonation token
      const { data, error } = await supabase.functions.invoke('impersonate-user', {
        body: { userId }
      });

      if (error) {
        logger.error("Impersonation failed:", error);
        toast({
          title: "Errore di impersonazione",
          description: error.message || "Non è stato possibile impersonare l'utente",
          variant: "destructive",
        });
        return { success: false };
      }

      if (!data?.access_token || !data?.refresh_token || !data?.session_id) {
        toast({
          title: "Errore",
          description: "Dati di sessione non validi",
          variant: "destructive",
        });
        return { success: false };
      }

      // Save admin session tokens for restore after impersonation ends
      const { data: currentSession } = await supabase.auth.getSession();
      if (currentSession.session) {
        localStorage.setItem('impersonation_admin_session', JSON.stringify({
          access_token: currentSession.session.access_token,
          refresh_token: currentSession.session.refresh_token,
        }));
      }

      // Skip signOut — setSession() overwrites the current session without
      // revoking admin tokens, so they remain valid for restore later.
      setImpersonationInProgress(true);

      // CLEAR OFFLINE CACHES (P1 DATA ISOLATION FIX)
      // Evita che i dati dell'admin si mescolino in cache locale con quelli dell'utente impersonato.
      const keysToRemove = ['cfa-override', 'pending_biglietti', 'shop-recent-visits'];
      keysToRemove.forEach(key => localStorage.removeItem(key));
      sessionStorage.removeItem('pwa-pin-verified');
      sessionStorage.removeItem('edu-area-choice');
      sessionStorage.removeItem('sa-area-choice');
      try {
        const dbNames = ['pasture-offline-db', 'idb-keyval', 'kiosk-offline-db'];
        for (const dbName of dbNames) {
          indexedDB.deleteDatabase(dbName);
        }
        logger.debug('🧹 impersonateUser: IndexedDB offline stores cleared');
      } catch (idbErr) {
        logger.warn('⚠️ impersonateUser: Failed to clear IndexedDB', idbErr);
      }

      // Set the new session with the impersonation tokens
      const { error: sessionError } = await supabase.auth.setSession({
        access_token: data.access_token,
        refresh_token: data.refresh_token
      });

      setImpersonationInProgress(false);

      if (sessionError) {
        logger.error("Failed to set session:", sessionError);
        toast({
          title: "Errore",
          description: "Impossibile impostare la sessione",
          variant: "destructive",
        });
        return { success: false };
      }

      logger.debug(`✅ Successfully impersonating user: ${data.target_user_email}`);

      toast({
        title: "Impersonazione attiva",
        description: `Stai operando come ${data.target_user_name || data.target_user_email}`,
      });

      // Return data for the caller to set impersonation state
      return {
        success: true,
        data: {
          session_id: data.session_id,
          target_user_id: data.target_user_id,
          target_user_name: data.target_user_name,
          target_user_email: data.target_user_email,
          admin_email: data.admin_email,
          expires_at: data.expires_at
        }
      };

    } catch (error) {
      setImpersonationInProgress(false);
      logger.error("Errore durante l'impersonazione:", error);
      toast({
        title: "Errore",
        description: "Si è verificato un errore imprevisto durante l'impersonazione",
        variant: "destructive",
      });
      return { success: false };
    }
  };

  // Logout
  const signOut = async () => {
    try {
      logger.debug('🚀 signOut: Starting logout process');

      // Detect device session BEFORE clearing localStorage
      const isDeviceSession = localStorage.getItem('device-session') === 'true';

      // Flush and stop the timing batcher
      stopTimingBatcher();

      // Clear all local storage data related to auth and offline state
      const keysToRemove = ['sb-obsjrmhaaftberxasdlr-auth-token', 'cfa-override', 'pending_biglietti', 'shop-recent-visits'];
      keysToRemove.forEach(key => {
        localStorage.removeItem(key);
      });
      sessionStorage.removeItem('pwa-pin-verified');
      sessionStorage.removeItem('edu-area-choice');

      // Clear IndexedDB stores containing PII (decrypted profiles, cached queries)
      try {
        const dbNames = ['pasture-offline-db', 'idb-keyval', 'kiosk-offline-db'];
        for (const dbName of dbNames) {
          indexedDB.deleteDatabase(dbName);
        }
        logger.debug('🧹 signOut: IndexedDB stores cleared');
      } catch (idbErr) {
        logger.warn('⚠️ signOut: Failed to clear IndexedDB', idbErr);
      }
      
      // Sign out from Supabase
      const { error } = await supabase.auth.signOut();

      // Remove device-session AFTER signOut so the SIGNED_OUT handler can still read it
      localStorage.removeItem('device-session');
      localStorage.removeItem('device-operator-id');
      localStorage.removeItem('device-operator-name');

      if (error) {
        logger.error('❌ signOut: Error from Supabase', error);
        throw error;
      }
      
      logger.debug('✅ signOut: Logout successful');
      
      toast({
        title: "Logout effettuato con successo",
        description: "Hai effettuato il logout dalla tua sessione",
      });
      
      // Redirect to /device (PIN screen) for device sessions, /auth for normal sessions
      const redirectPath = isDeviceSession ? '/device' : '/auth';
      setTimeout(() => {
        window.location.href = redirectPath;
      }, 500);

    } catch (error) {
      logger.error("Errore durante il logout:", error);

      const wasDeviceSession = localStorage.getItem('device-session') === 'true';
      localStorage.clear();

      toast({
        title: "Logout completato",
        description: "La sessione è stata terminata",
      });

      setTimeout(() => {
        window.location.href = wasDeviceSession ? '/device' : '/auth';
      }, 500);
    }
  };

  const value = {
    signIn,
    signUp,
    signOut,
    loginAsTestUser,
    impersonateUser
  };

  return <AuthMethodsContext.Provider value={value}>{children}</AuthMethodsContext.Provider>;
};

/**
 * @description Hook to access authentication methods from AuthMethodsContext.
 * @returns {{ signIn: Function, signUp: Function, signOut: Function, loginAsTestUser: Function, impersonateUser: Function }}
 */
export const useAuthMethods = () => {
  const context = useContext(AuthMethodsContext);
  if (context === undefined) {
    throw new Error("useAuthMethods deve essere utilizzato all'interno di un AuthMethodsProvider");
  }
  return context;
};
